import { MaintenanceState, MaintenanceStateInfo } from '@mp/system/maintenance/domain';
import {
  computeServerTimeOffset,
  maintenanceFastTickThresholdMs,
  msUntilNextTransition,
} from '@mp/system/maintenance/util';

/**
 * The polling interval while no window is disclosed.
 */
export const maintenanceStateIdleRefetchIntervalMs = 300_000;

/**
 * The polling interval outside of a window edge.
 */
export const maintenanceStateDefaultRefetchIntervalMs = 60_000;

/**
 * The shortest polling interval near a window edge.
 */
export const maintenanceStateMinRefetchIntervalMs = 5_000;

/**
 * The longest polling interval near a window edge.
 */
export const maintenanceStateMaxRefetchIntervalMs = 30_000;

/**
 * Calculates the polling interval of the maintenance state query.
 *
 * Near a window edge the interval is tightened so that lock and unlock happen within seconds. The client
 * clock is corrected by the server time offset of the last response.
 *
 * Im Zustand `None` wird alle fünf Minuten gefragt, und das ist eine bewusste Abwägung - kein Versehen und
 * kein Defekt:
 *
 * - Ein Fenster wird lange im Voraus angekündigt (Vorlauf üblicherweise Stunden bis Tage). Der Client kann
 *   die Ankündigung nicht vorausberechnen, weil der Server ein noch nicht angekündigtes Fenster bewusst
 *   überhaupt nicht herausgibt (Zustand `None`, keine Zeiten, kein Text) - er erfährt sie ausschließlich
 *   durch Nachfragen. Das Ankündigungsbanner erscheint deshalb im schlechtesten Fall ein Pollintervall, also
 *   bis zu fünf Minuten, nach dem Speichern des Fensters. Gemessen am Vorlauf ist das bedeutungslos.
 * - Die SPERRE hängt nie an diesem Poll: sie wird sofort geschoben, sobald ein Request am markierten
 *   `x-mediio-maintenance`-503 scheitert. Ein zu großes Intervall kann also niemals einen Benutzer
 *   weiterarbeiten lassen, während die Wartung läuft.
 * - Der Zustandsendpunkt ist `[Authorize]`: jeder Poll durchläuft die vollständige Anmeldekette samt
 *   Mongo-Aggregation. Ein Minutentakt kostet 60 Anfragen pro Stunde und Tab, um ein Ereignis zu entdecken,
 *   das ein paar Mal im Jahr eintritt.
 *
 * Wer hier verkürzt, bezahlt Dauerlast für Sekunden Vorwarnung; wer verlängert, verzögert nur die Anzeige der
 * Ankündigung. Beides ist keine Verbesserung.
 *
 * Eine Ausnahme vom Ruhetakt ist eine LAUFENDE Sperre (`isLockRequired`): bricht ein Admin ein Fenster mit
 * noch Stunden verbleibender Laufzeit vorzeitig ab, sagt die letzte bekannte Restzeit fälschlich "weit weg",
 * und der Takt bliebe sonst bei 5 Minuten stehen. Der gesperrte Bildschirm lässt keine anderen Requests mehr
 * zu, die sonst über den markierten 503 sofort freischalten würden - der Poll ist dann die einzige
 * Möglichkeit, die Aufhebung zu bemerken.
 *
 * Angehoben wird dabei auf den DEFAULT-Takt (60s) und NIE darunter, auch nicht nahe der tatsächlichen
 * Umschaltzeit: die Verengung dort unten dient einer akkuraten, aber pro Sitzung UNGESTREUTEN Restzeit - genau
 * derselben Sekunde für alle gesperrten Sitzungen. Ließe man sie während der Sperre wirken, poll'te praktisch
 * jede Sitzung das tatsächliche Ende innerhalb weniger Sekunden ab und löste darüber sofort den Reload in
 * `MaintenanceLockService.synchronizeLock` aus - unstreut, und meist BEVOR die eigens für den Reload
 * eingeführte Streuung (`MAINTENANCE_TRANSITION_REFETCH_DELAY_MS`, 0-30s je Sitzung) überhaupt greift, die
 * genau diese gleichzeitige Neuladewelle verhindern soll. Die Erkennung der Umschaltzeit selbst hängt während
 * einer Sperre ohnehin nicht an diesem Intervall: `MaintenanceLockService` taktet dafür lokal jede Sekunde
 * (`maintenanceTickIntervalMs`), unabhängig vom Netzwerk-Poll.
 * @param stateInfo The last known maintenance state, `null` while it has not been loaded.
 * @param dataUpdatedAt The client timestamp (epoch milliseconds) of the last response.
 * @param isLockRequired Whether the lock is currently shown to this user.
 * @param now The current client time (epoch milliseconds).
 * @returns The interval in milliseconds.
 */
export function calculateMaintenanceStateRefetchInterval(
  stateInfo: MaintenanceStateInfo | null,
  dataUpdatedAt: number,
  isLockRequired: boolean,
  now: number = Date.now(),
): number {
  // Während einer Sperre gilt ausnahmslos der Default-Takt - unabhängig von Zustand und Nähe zur
  // Umschaltzeit, siehe Begründung oben.
  if (isLockRequired) {
    return maintenanceStateDefaultRefetchIntervalMs;
  }

  if (!stateInfo || stateInfo.state === MaintenanceState.None) {
    return maintenanceStateIdleRefetchIntervalMs;
  }

  const correctedNow = now + computeServerTimeOffset(stateInfo.serverUtcNow, dataUpdatedAt);
  const untilTransition = msUntilNextTransition(stateInfo, correctedNow);

  // Ein angekündigtes Fenster liegt oft Tage in der Zukunft. Solange die Umschaltzeit weit weg ist, genügt
  // der Ruhetakt: sonst fiele für den ganzen Vorlauf genau die Dauerlast an, die der Zustand `None` oben
  // bewusst vermeidet. Die Schwelle ist dieselbe wie beim Countdown-Takt, weil es dieselbe Frage ist ("ist
  // die Umschaltzeit nah?"), und sie liegt weit über dem Ruhetakt - der Wechsel in den kurzen Takt kommt
  // also immer rechtzeitig vor der Umschaltzeit.
  if (untilTransition !== null && untilTransition > maintenanceFastTickThresholdMs) {
    return maintenanceStateIdleRefetchIntervalMs;
  }

  if (untilTransition == null || untilTransition >= maintenanceStateDefaultRefetchIntervalMs) {
    return maintenanceStateDefaultRefetchIntervalMs;
  }

  return Math.min(
    maintenanceStateMaxRefetchIntervalMs,
    Math.max(maintenanceStateMinRefetchIntervalMs, untilTransition),
  );
}
